import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import {
  OutreachContactsContext,
  ScheduledType,
} from "contexts/OutreachContacts";
import { useContext, useEffect } from "react";
import { useInView } from "react-intersection-observer";

import { usePageSize } from "utils/usePageSize";

import ScheduledContact from "../ScheduledContact";
import HalfScreenPreviewCard from "../ScheduledThreadPreview/HalfScreenPreviewCard";
import CancelAllButton from "./CancelAll";
import styles from "./styles";

interface Props {
  totalPages: number;
  isSmallScreen: boolean;
  sidebarOpen: boolean;
}
const REF_OFFSET = 9;

const ScheduledSidebar = ({
  totalPages,
  isSmallScreen,
  sidebarOpen,
}: Props) => {
  const { currentContact } = useContext(OutreachContactsContext);
  const [paginationRef, isPaginationRefVisible] = useInView({
    rootMargin: "0px 0px",
  });
  const {
    contacts,
    handleFetchContacts,
    fetchContactsParams,
    scheduledTotals,
    setCurrentContact,
    fetchMoreLoading,
    fetchLoading,
  } = useContext(OutreachContactsContext);

  const totalOutreach = scheduledTotals[ScheduledType.outreach] ?? 0;
  const totalFollowup = scheduledTotals[ScheduledType.followUp] ?? 0;

  const handleClick = (scheduledType: ScheduledType) => {
    setCurrentContact(null);
    const updatedParams = { ...fetchContactsParams, scheduledType, page: 1 };
    handleFetchContacts(updatedParams);
  };

  const handleRefresh = () => {
    setCurrentContact(null);
    handleFetchContacts({ ...fetchContactsParams, page: 1 });
  };

  const { hasBanner } = usePageSize();

  useEffect(() => {
    if (isPaginationRefVisible && fetchContactsParams.page < totalPages) {
      const updatedParams = { ...fetchContactsParams };
      updatedParams.page += 1;
      handleFetchContacts(updatedParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationRefVisible]);

  return (
    <Grid container sx={styles.container}>
      <Box sx={styles.header}>
        <Grid container alignItems="center" justifyContent={"space-between"}>
          {!isSmallScreen && (
            <>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ my: { xs: 0, md: 1.5 } }}
                >
                  Scheduled Emails
                </Typography>
                <Tooltip title="Refresh scheduled emails">
                  <IconButton
                    onClick={handleRefresh}
                    disabled={fetchLoading}
                    sx={{ color: "black", fontSize: 12, ml: 0.5 }}
                  >
                    {fetchLoading ? (
                      <CircularProgress size={10} />
                    ) : (
                      <Box component="i" className="fa-regular fa-refresh" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
              <CancelAllButton />
            </>
          )}
        </Grid>

        <Grid container sx={{ ml: -1 }} justifyContent={"space-between"}>
          <Box>
            <Button
              onClick={() => handleClick(ScheduledType.outreach)}
              sx={[
                styles.buttonTab,
                fetchContactsParams.scheduledType === ScheduledType.outreach
                  ? styles.active
                  : {},
              ]}
            >
              Outreach ({totalOutreach})
            </Button>
            <Button
              onClick={() => handleClick(ScheduledType.followUp)}
              sx={[
                styles.buttonTab,
                fetchContactsParams.scheduledType === ScheduledType.followUp
                  ? styles.active
                  : {},
              ]}
            >
              Follow-ups ({totalFollowup})
            </Button>
          </Box>
        </Grid>
      </Box>

      <Box sx={styles.body(hasBanner)}>
        {contacts?.length === 0 && (
          <Typography textAlign="center" sx={{ color: grey[500], p: 3 }}>
            You don't have any scheduled {fetchContactsParams.scheduledType} at
            this time.
          </Typography>
        )}
        {contacts?.map((contact, index) => (
          <Box
            key={contact.id}
            ref={
              (
                REF_OFFSET > contacts?.length
                  ? contacts.length - 1 === index
                  : contacts.length - REF_OFFSET === index
              )
                ? paginationRef
                : null
            }
          >
            <ScheduledContact contact={contact} />
          </Box>
        ))}
        {fetchMoreLoading && (
          <Box sx={{ p: 2 }}>
            {Array.from(new Array(5)).map((_, index) => (
              <Skeleton
                key={index}
                animation="wave"
                width="100%"
                height="150px"
                sx={{ mt: -6 }}
              />
            ))}
          </Box>
        )}
      </Box>

      {isSmallScreen && !sidebarOpen && currentContact?.id && (
        <HalfScreenPreviewCard />
      )}
    </Grid>
  );
};

export default ScheduledSidebar;
